import React, { useCallback } from 'react';
import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { useDropzone } from 'react-dropzone';

export default function DropZone({ imagesData, setImagesData }) {
  const onDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const base64Image = String(reader.result).split(',')[1];
          setImagesData((current) => [
            ...current,
            {
              fileName: file.path || file.name,
              position: null,
              binary: base64Image,
              preview: URL.createObjectURL(file),
              id: null
            }
          ]);
        };
      });
    },
    [setImagesData]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: true,
    accept: { 'image/jpeg': ['.jpeg', '.jpg'], 'image/png': ['.png'] },
    maxFiles: 15,
    noClick: true
  });

  const handleDelete = (image) => {
    if (image.preview) URL.revokeObjectURL(image.preview);
    setImagesData((current) => current.filter((item) => item !== image));
  };

  const handlePositionChange = (event, index) => {
    setImagesData((current) =>
      current.map((image, imageIndex) =>
        imageIndex === index ? { ...image, position: event.target.value } : image
      )
    );
  };

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          alignItems: 'center',
          bgcolor: isDragActive ? 'primary.light' : '#fafbfc',
          border: '1.5px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: 180,
          outline: 'none',
          p: 3,
          textAlign: 'center',
          transition: 'all 180ms ease'
        }}
      >
        <input {...getInputProps()} />
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: 'primary.light',
            borderRadius: 3,
            color: 'primary.main',
            display: 'flex',
            height: 48,
            justifyContent: 'center',
            mb: 1.5,
            width: 48
          }}
        >
          <AddPhotoAlternateRoundedIcon />
        </Box>
        <Typography fontWeight={700}>
          {isDragActive ? 'Пуснете снимките тук' : 'Плъзнете снимки тук'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          JPG или PNG · до 15 файла
        </Typography>
        <Button type="button" variant="outlined" onClick={open}>
          Избери файлове
        </Button>
      </Box>

      {imagesData.length > 0 && (
        <Stack spacing={1.25} mt={2}>
          {imagesData.map((image, index) => (
            <Box
              key={`${image.fileName}-${index}`}
              sx={{
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2.5,
                display: 'grid',
                gap: 1,
                gridTemplateColumns: '52px minmax(0, 1fr) 92px auto',
                p: 1
              }}
            >
              <Box
                component="img"
                src={image.preview}
                alt={image.fileName}
                sx={{ borderRadius: 1.5, height: 52, objectFit: 'cover', width: 52 }}
              />
              <Typography variant="body2" noWrap title={image.fileName}>
                {image.fileName}
              </Typography>
              <TextField
                label="Позиция"
                type="number"
                value={image.position ?? ''}
                onChange={(event) => handlePositionChange(event, index)}
              />
              <Tooltip title="Премахни">
                <IconButton
                  color="error"
                  onClick={() => handleDelete(image)}
                  aria-label="Премахни снимката"
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
